import { DashboardT } from 'business/definitions';
import { QueryClient } from '@tanstack/react-query';
import { FAVOURITE_DASHBOARDS_QUERY_KEY } from '../hooks/favouriteDashboards';

/**
 * Add a method to sync the React Query Data in the homepage. We need this because
 * the DashboardEditMenu could update one of the dashboard props. So we need to keep
 * the different RQ Calls valid.
 * @param  {DashboardT} dashboard - The current dashboard
 * @param {keyof DashboardT} property - The property to be toggled in the cache
 * @param {boolean} value - The boolean value to be set in the cache
 * @param {QueryClient} queryClient - React Query client.
 * @param {string[]} cacheKeys - The cache keys of the data that needs to be synced.
 * @return {DashboardT[]} dashboards - Return the new dashboards data
 */
export const syncCacheOnPropertyChange = (
    dashboard: DashboardT,
    property: keyof DashboardT,
    value: boolean,
    queryClient: QueryClient,
    cacheKeys: string[],
) => {
    cacheKeys.forEach((cacheKey) => {
        // We don't want to toggle the isUserFavourite property because the dashboard is not on the list
        // but we want to push the actual dashboard in the data if the dashboard has been marked as favourite
        // (and remove in the opposite way)
        if (
            cacheKey === FAVOURITE_DASHBOARDS_QUERY_KEY &&
            property === 'isUserFavourite'
        ) {
            queryClient.setQueryData([cacheKey], (dashboards: DashboardT[]) => {
                if (value) {
                    return [
                        ...dashboards,
                        { ...dashboard, isUserFavourite: value },
                    ];
                }
                return dashboards.filter((d) => d.key !== dashboard.key);
            });
        } else {
            queryClient.setQueryData([cacheKey], (dashboards: DashboardT[]) =>
                dashboards.map((d) => {
                    if (d.slug === dashboard.slug) {
                        return {
                            ...d,
                            [property]: value,
                        };
                    }
                    return d;
                }),
            );
        }
    });
};
